import { z } from 'zod';

import { callDirectionSchema, callTransportSchema } from './call.schema';

/** Snapshot rendered as the second line of a WhatsApp-style contact row. */
export const lastInteractionPreviewSchema = z.object({
  type: z.enum(['message', 'call']),
  channel: z.string().nullable().optional(),
  direction: z.string().nullable().optional(),
  text: z.string().nullable().optional(),
  outcome: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  at: z.string(),
});

export type LastInteractionPreview = z.infer<typeof lastInteractionPreviewSchema>;

export const contactSchema = z.object({
  id: z.uuid(),
  name: z.string().min(2),
  phone: z.string().min(5).nullable().optional(),
  email: z.string().email().nullable().optional(),
  unique_identifier: z.string().optional(),
  data: z.record(z.string(), z.unknown()).optional().nullable(),
  tags: z.array(z.string()).default([]),
  last_interaction_at: z.date().or(z.string().datetime()).nullable().optional(),
  last_interaction_preview: lastInteractionPreviewSchema.nullable().optional(),
  created_at: z.date().or(z.string().datetime()).optional(),
  updated_at: z.date().or(z.string().datetime()).optional(),
});

export type Contact = z.infer<typeof contactSchema>;

export const contactCallSummarySchema = z.object({
  id: z.string().uuid(),
  direction: callDirectionSchema,
  call_transport: callTransportSchema,
  status: z.string(),
  outcome: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  phone_number: z.string().nullable().optional(),
  created_at: z.date().or(z.string().datetime()),
  campaign: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
});

export type ContactCallSummary = z.infer<typeof contactCallSummarySchema>;

export const contactConversationSummarySchema = z.object({
  id: z.string().uuid(),
  channel: z.enum(['WHATSAPP', 'SMS', 'EMAIL', 'WEB_WIDGET']),
  status: z.enum(['OPEN', 'CLOSED', 'ARCHIVED']),
  configuration_id: z.string().uuid().nullable().optional(),
  last_message_at: z.date().or(z.string().datetime()).nullable().optional(),
  last_message_preview: z.string().nullable().optional(),
  unread_count: z.number(),
  remote_address: z.string(),
  created_at: z.date().or(z.string().datetime()),
});

export type ContactConversationSummary = z.infer<typeof contactConversationSummarySchema>;

export const contactWithCommunicationsSchema = contactSchema.extend({
  // Deprecated: detail no longer returns calls (history = timeline endpoint).
  calls: z.array(contactCallSummarySchema).optional(),
  messaging_conversations: z.array(contactConversationSummarySchema),
});

export type ContactWithCommunications = z.infer<typeof contactWithCommunicationsSchema>;

/** Platform user a contact's conversation is assigned to / claimed by. */
export const contactAssignedUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
});

export type ContactAssignedUser = z.infer<typeof contactAssignedUserSchema>;

export const contactWithCountsSchema = contactSchema.extend({
  _count: z.object({
    calls: z.number(),
    messaging_conversations: z.number(),
  }),
  unread_count: z.number().default(0),
  /** Distinct users assigned to any of the contact's conversations. */
  assigned_users: z.array(contactAssignedUserSchema).default([]),
  /** Free-form tags stored in the contacts.tags column. */
  tags: z.array(z.string()).default([]),
  /** First configuration_id from the contact's conversations (for calling). */
  configuration_id: z.string().uuid().nullable().default(null),
});

export type ContactWithCounts = z.infer<typeof contactWithCountsSchema>;

export const updateContactSchema = z.object({
  name: z.string().min(2).max(255).optional(),
  phone: z.string().min(5).max(50).nullable().optional(),
  email: z.string().email().max(255).nullable().optional(),
  data: z.record(z.string(), z.unknown()).optional().nullable(),
  tags: z.array(z.string()).optional(),
});

export type UpdateContactDto = z.infer<typeof updateContactSchema>;

export const contactQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  assignedUserId: z.string().uuid().optional(),
  unassigned: z.coerce.boolean().optional(),
  tag: z.string().optional(),
  /** Comma-separated tags — ORed within a tag type, ANDed across types. */
  tags: z.string().optional(),
  /** When true, only return contacts that have at least one INBOUND message. */
  hasInbound: z.coerce.boolean().optional(),
  /** Only return contacts that have a conversation on this channel. */
  channel: z.enum(['WHATSAPP', 'SMS', 'EMAIL', 'WEB_WIDGET']).optional(),
  /** Only return contacts with a tool invocation in the last 7 days. */
  activeTools: z.coerce.boolean().optional(),
});

export type ContactQuery = z.infer<typeof contactQuerySchema>;

export const mergeContactsSchema = z
  .object({
    primary_id: z.string().uuid(),
    duplicate_ids: z.array(z.string().uuid()).min(1).max(50),
  })
  .refine((value) => !value.duplicate_ids.includes(value.primary_id), {
    message: 'primary_id cannot be one of duplicate_ids',
  });

export type MergeContactsDto = z.infer<typeof mergeContactsSchema>;

export const duplicateGroupContactSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  created_at: z.date().or(z.string().datetime()),
  calls_count: z.number(),
  conversations_count: z.number(),
  has_identifier: z.boolean(),
});

export type DuplicateGroupContact = z.infer<typeof duplicateGroupContactSchema>;

export const contactDuplicateGroupSchema = z.object({
  match_types: z.array(z.enum(['phone', 'email'])),
  suggested_primary_id: z.string().uuid(),
  contacts: z.array(duplicateGroupContactSchema),
});

export type ContactDuplicateGroup = z.infer<typeof contactDuplicateGroupSchema>;

export interface MergeContactsResult {
  primary_id: string;
  merged_contact_ids: string[];
  conversations_merged: number;
}

export interface DeduplicateContactsResult {
  contacts_normalized: number;
  groups_merged: number;
  contacts_merged: number;
  conversations_merged: number;
  secondary_numbers_recovered?: number;
}

export const contactCleanupModeSchema = z.enum(['merge-only', 'full']);

export type ContactCleanupMode = z.infer<typeof contactCleanupModeSchema>;

export const startContactCleanupSchema = z
  .object({
    mode: contactCleanupModeSchema.default('full'),
  })
  .default({ mode: 'full' });

export type StartContactCleanupDto = z.infer<typeof startContactCleanupSchema>;

// --- Unified contact timeline (messages + voice calls interleaved) ---

export const timelineMessageItemSchema = z.object({
  kind: z.literal('message'),
  id: z.string().uuid(),
  timestamp: z.string(),
  conversation_id: z.string().uuid(),
  channel: z.enum(['WHATSAPP', 'SMS', 'EMAIL', 'WEB_WIDGET']),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  status: z.enum(['PENDING', 'SENT', 'DELIVERED', 'READ', 'FAILED']),
  type: z.string(),
  content: z.string().nullable(),
  media_url: z.string().nullable(),
  error_message: z.string().nullable().optional(),
  reply_to_message_id: z.string().uuid().nullable().optional(),
  reply_to_content: z.string().nullable().optional(),
  reply_to_direction: z.enum(['INBOUND', 'OUTBOUND']).nullable().optional(),
});

export type TimelineMessageItem = z.infer<typeof timelineMessageItemSchema>;

export const timelineCallItemSchema = z.object({
  kind: z.literal('call'),
  id: z.string().uuid(),
  timestamp: z.string(),
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  call_transport: z.string(),
  status: z.string(),
  outcome: z.string().nullable(),
  duration: z.number().nullable(),
  campaign_name: z.string().nullable(),
});

export type TimelineCallItem = z.infer<typeof timelineCallItemSchema>;

/** Agent tool invocation recorded on a call transcript (metadata.type = 'tool_call'). */
export const timelineToolItemSchema = z.object({
  kind: z.literal('tool'),
  id: z.string(),
  timestamp: z.string(),
  call_id: z.string().uuid(),
  tool_name: z.string(),
  arguments: z.record(z.string(), z.unknown()).nullable(),
});

export type TimelineToolItem = z.infer<typeof timelineToolItemSchema>;

export const timelineItemSchema = z.discriminatedUnion('kind', [
  timelineMessageItemSchema,
  timelineCallItemSchema,
  timelineToolItemSchema,
]);

export type TimelineItem = z.infer<typeof timelineItemSchema>;

export const contactTimelineQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  /** When true, tool-call events from call transcripts are interleaved as kind 'tool'. */
  include_tool_calls: z.coerce.boolean().optional(),
});

export type ContactTimelineQuery = z.infer<typeof contactTimelineQuerySchema>;

export interface ContactTimelineResponse {
  /** Newest-first. The UI renders bottom-anchored and prepends older pages. */
  items: TimelineItem[];
  /** Pass back as ?cursor= to fetch the next (older) page. Null = no more. */
  next_cursor: string | null;
}

export interface ContactCleanupStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  mode?: ContactCleanupMode;
  stage?: string;
  groups_total?: number;
  groups_processed?: number;
  started_at?: string;
  finished_at?: string;
  result?: DeduplicateContactsResult;
  error?: string;
}
